import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Eye, EyeOff, FolderOpen, Paperclip, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useAddDocumentMutation, useDeleteDocumentMutation, useDocumentsQuery, useOpenSigned, useUpdateDocumentMutation } from '@/client/api/hr/records';
import { HR_DOCUMENT_KIND_LABEL, type HrDocument, type HrDocumentKind } from '@/client/types/hr';
import { HrPageShell } from './components/HrPageShell';
import { DeleteConfirmDialog, EmployeeLink, EmptyState, RowMenu, StatusBadge } from './components/hr-ui';
import { formatDay } from './components/leave-format';
import { useEmployees } from './hr-store';

const inputClass = 'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';
const filterClass = 'h-9 rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

/** Files about employees (contracts, IDs, tax forms) in private storage. HR
 *  chooses per file whether the employee sees it in My workplace. */
export default function EmployeeDocumentsPage() {
  const employees = useEmployees();
  const [employeeId, setEmployeeId] = useState('');
  const docsQ = useDocumentsQuery(employeeId || undefined);
  const add = useAddDocumentMutation();
  const update = useUpdateDocumentMutation();
  const remove = useDeleteDocumentMutation();
  const open = useOpenSigned('hr');
  const canManage = useGate(PERMISSIONS.hr.manage).allowed;
  const fileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<{ employeeId: string; kind: HrDocumentKind; name: string; visible: boolean; expiresOn: string; file: File | null } | null>(null);
  const [deleting, setDeleting] = useState<HrDocument | null>(null);
  const byId = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const docs = docsQ.data ?? [];
  const soon = new Date(Date.now() + 60 * 86_400_000).toISOString().slice(0, 10);

  const upload = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form?.file || !form.employeeId) {
      toast.error('Pick the employee and a file');
      return;
    }
    add.mutate(
      { employeeId: form.employeeId, file: form.file, kind: form.kind, name: form.name || form.file.name, visibleToEmployee: form.visible, expiresOn: form.expiresOn || undefined },
      {
        onSuccess: () => {
          toast.success('Uploaded');
          setForm(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Upload failed'),
      },
    );
  };

  return (
    <HrPageShell
      mock={false}
      title="Employee files"
      description="Contracts, IDs, tax forms and certificates, kept in private storage. Files open through a link that works for 10 minutes."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Documents' }, { label: 'Employee files' }]}
      icon={FolderOpen}
      actions={
        <PermissionButton
          code={PERMISSIONS.hr.manage}
          className="gap-2"
          onClick={() => setForm({ employeeId, kind: 'contract', name: '', visible: true, expiresOn: '', file: null })}
        >
          <Upload className="h-4 w-4" /> Upload file
        </PermissionButton>
      }
      stats={[
        { key: 'files', label: 'Files', value: docs.length },
        { key: 'hidden', label: 'HR only', value: docs.filter((d) => !d.visibleToEmployee).length },
        { key: 'expiring', label: 'Expiring in 60 days', value: docs.filter((d) => d.expiresOn && d.expiresOn <= soon).length },
      ]}
    >
      <div className="mb-4">
        <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className={filterClass}>
          <option value="">Everyone</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>
      </div>

      {docsQ.isError ? (
        <EmptyState label={`Could not load files: ${(docsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : docsQ.isLoading ? (
        <EmptyState label="Loading files…" />
      ) : docs.length === 0 ? (
        <EmptyState label="No files yet." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">File</th>
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 font-medium">Kind</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3 font-medium">Seen by employee</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {docs.map((d) => (
                <tr key={d.id} className="cursor-pointer hover:bg-gray-50" onClick={() => open(`/documents/${d.id}/download`).catch((e) => toast.error((e as Error).message))}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{d.name}</p>
                    {d.uploadedAt && <p className="text-xs text-gray-400">uploaded {formatDay(d.uploadedAt)}</p>}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <EmployeeLink employee={byId.get(d.employeeId)} />
                  </td>
                  <td className="px-4 py-3 text-gray-600">{HR_DOCUMENT_KIND_LABEL[d.kind]}</td>
                  <td className={`px-4 py-3 ${d.expiresOn && d.expiresOn <= soon ? 'font-medium text-amber-700' : 'text-gray-600'}`}>
                    {d.expiresOn ? formatDay(d.expiresOn) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge label={d.visibleToEmployee ? 'Yes' : 'HR only'} className={d.visibleToEmployee ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      items={[
                        { label: 'Open', icon: Paperclip, onSelect: () => open(`/documents/${d.id}/download`).catch((e) => toast.error((e as Error).message)) },
                        {
                          label: d.visibleToEmployee ? 'Hide from employee' : 'Show to employee',
                          icon: d.visibleToEmployee ? EyeOff : Eye,
                          hidden: !canManage,
                          onSelect: () =>
                            update.mutate(
                              { id: d.id, visibleToEmployee: !d.visibleToEmployee },
                              { onSuccess: () => toast.success('Saved'), onError: (e) => toast.error((e as Error).message) },
                            ),
                        },
                        { label: 'Delete', icon: Trash2, danger: true, hidden: !canManage, onSelect: () => setDeleting(d) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload a file</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={upload} className="space-y-4">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Employee</span>
                <select required value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })} className={inputClass}>
                  <option value="">Select someone</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-gray-600">Kind</span>
                  <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as HrDocumentKind })} className={inputClass}>
                    {Object.entries(HR_DOCUMENT_KIND_LABEL).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-gray-600">Expires (optional)</span>
                  <input type="date" value={form.expiresOn} onChange={(e) => setForm({ ...form, expiresOn: e.target.value })} className={inputClass} />
                </label>
              </div>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Name (optional)</span>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} placeholder={form.file?.name ?? 'Employment contract 2026'} />
              </label>
              <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] ?? null })} />
              <Button type="button" variant="outline" className="w-full gap-2" onClick={() => fileRef.current?.click()}>
                <Upload className="h-4 w-4" /> {form.file ? form.file.name : 'Choose a PDF or photo (up to 10 MB)'}
              </Button>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} /> The employee can see it in My workplace
              </label>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={add.isPending}>
                  {add.isPending ? 'Uploading…' : 'Upload'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this file?"
        description={`"${deleting?.name}" is removed from ${byId.get(deleting?.employeeId ?? '')?.name ?? 'their'} record.`}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id, { onSuccess: () => toast.success('Deleted'), onError: (e) => toast.error((e as Error).message) });
          setDeleting(null);
        }}
      />
    </HrPageShell>
  );
}
